/**
 * Full-screen dialog for editing a single animal group's feed budget.
 *
 * The chart and data rows share a single HTML table — bars are rendered as
 * styled divs inside individual cells, with the demand line as an SVG overlay.
 * Column hover highlights the entire month column across chart + data rows.
 *
 * First two columns (label + Y-axis spacer) are sticky so they stay visible
 * when the user scrolls horizontally through the 12 month columns.
 */

import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PrintIcon from "@mui/icons-material/Print";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import { COLORS } from "app/theme";
import DetailDialog from "common/components/DetailDialog";
import { useFields } from "model";
import { useCallback, useEffect, useMemo, useState } from "react";
import AssignFieldDialog from "../animalGroupDetail/AssignFieldDialog";
import AddStockClassDialog from "./AddStockClassDialog";
import {
  CONCENTRATES_FEED_TYPE_ID,
  DEFAULT_FIELD_FEEDING_UTILISATION_PERCENT,
  DEFAULT_GRAZING_DAYS,
  DEFAULT_GRAZING_FIELD_UTILISATION_PERCENT,
  getDefaultHousedUtilisation,
  ROUGHAGE_SUPPLEMENT_OPTIONS,
} from "./constants";
import EditableCell from "./EditableCell";
import FeedingSheetPrintDialog from "./FeedingSheetPrintDialog";
import StockClassMonthlyTable from "./StockClassMonthlyTable";
import {
  BudgetTable,
  LabelCell,
  MonthHeaderCell,
  ValueCell,
} from "./tableStyles";
import type {
  AnimalGroupBudget,
  AnimalGroupComputedRow,
  FeedBudgetPlan,
  StockClassComputedMonth,
  StockClassMonthEntry,
} from "./types";
import { MONTH_LABELS } from "./types";

const CHART_HEIGHT = 180;
const SECTION_CARD_SX = {
  backgroundColor: "white",
  borderRadius: "16px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  p: { xs: 1.5, sm: 2 },
  mb: 2,
} as const;

const METRIC_CARD_SX = {
  backgroundColor: "white",
  borderRadius: 2,
  px: 1.25,
  py: 0.4,
  border: "1px solid #D3C9E7",
} as const;

/** Hover highlight color for active column */
const COL_HOVER_BG = "rgba(0,0,0,0.03)";

/** sx to remove borders from data cells in the summary table */
const noBorder = {
  borderBottom: "none",
  borderTop: "none",
  borderRight: "none",
} as const;

/** Sticky sx for the Y-axis spacer column (second column). */
const stickySpacerSx = {
  position: "sticky" as const,
  left: { xs: 108, sm: 170 },
  backgroundColor: "#fff",
  zIndex: 10,
  width: 40,
  p: 0,
};

/**
 * Wraps a group of rows in a colored border with rounded corners.
 * Renders as a <tbody> so rows stay in the flat table — sticky columns work.
 * Uses CSS selectors to apply colored borders to the first/last row/cell.
 */
function ColorGroup({
  color,
  children,
}: {
  color: string;
  children: React.ReactNode;
}) {
  const b = `2px solid ${color}`;
  // nth-of-type(n+3) targets month cells only (skipping sticky label + spacer)
  return (
    <Box
      component="tbody"
      sx={{
        // Reset all cell borders
        "& > tr > td": {
          border: "none !important",
        },
        // Top border on first row
        "& > tr:first-of-type > td": {
          borderTop: `${b} !important`,
        },
        // Bottom border on last row
        "& > tr:last-of-type > td": {
          borderBottom: `${b} !important`,
        },
        // Left border on first cell (label)
        "& > tr > td:first-of-type": {
          borderLeft: `${b} !important`,
        },
        // Right border on last cell
        "& > tr > td:last-of-type": {
          borderRight: `${b} !important`,
        },
        // Rounded corners — top
        "& > tr:first-of-type > td:first-of-type": {
          borderTopLeftRadius: 8,
        },
        "& > tr:first-of-type > td:last-of-type": {
          borderTopRightRadius: 8,
        },
        // Rounded corners — bottom
        "& > tr:last-of-type > td:first-of-type": {
          borderBottomLeftRadius: 8,
        },
        "& > tr:last-of-type > td:last-of-type": {
          borderBottomRightRadius: 8,
        },
      }}
    >
      {children}
    </Box>
  );
}

/** Spacer tbody between groups — with sticky first two cells */
function SpacerSection({
  hoverCol,
  enterCol,
  leaveCol,
  colCount,
}: {
  hoverCol: number | null;
  enterCol: (i: number) => void;
  leaveCol: () => void;
  colCount: number;
}) {
  return (
    <TableBody>
      <TableRow>
        <TableCell
          colSpan={2}
          sx={{
            p: 0,
            height: 12,
            border: "none",
            position: "sticky",
            left: 0,
            backgroundColor: "#fff",
            zIndex: 10,
          }}
        />
        {Array.from({ length: colCount }, (_, i) => (
          <TableCell
            key={i}
            onMouseEnter={() => enterCol(i)}
            onMouseLeave={leaveCol}
            sx={{
              p: 0,
              height: 12,
              border: "none",
              backgroundColor: hoverCol === i ? COL_HOVER_BG : "transparent",
              transition: "background-color 0.1s",
            }}
          />
        ))}
      </TableRow>
    </TableBody>
  );
}

interface FeedStockRow {
  type: string;
  stockTonnes: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  budget: AnimalGroupBudget;
  grazingMonths: FeedBudgetPlan["grazingMonths"];
  groupRows: AnimalGroupComputedRow[];
  /** Year-1 computed rows for when viewYear > planStartYear */
  groupRowsYear1?: AnimalGroupComputedRow[];
  stockClassComputed: Map<string, StockClassComputedMonth[]>;
  /** Year-1 computed stock class months */
  stockClassComputedYear1?: Map<string, StockClassComputedMonth[]>;
  actualGrowthRatesKgHaDay: number[];
  currentMonth: number;
  feedStock?: FeedStockRow[];
  planName?: string;
  planYear?: number;
  planStartDate?: string;
  planEndDate?: string;
  initialViewYear?: number;
  /** Called when the farmer wants to extend the plan end date */
  onExtendPlan?: (newEndDate: string) => void;
  onUpdateStockClass: (
    animalGroupId: string,
    stockClassId: string,
    month: number,
    yearOffset: 0 | 1,
    field: keyof Pick<
      StockClassMonthEntry,
      "animalCount" | "averageWeightKg" | "allocationPercent"
    >,
    value: number
  ) => void;
  onUpdateSupplementAmount: (
    animalGroupId: string,
    month: number,
    feedTypeId: string,
    value: number
  ) => void;
  onUpdateSupplementUtilisation: (
    animalGroupId: string,
    utilisationPercent: number
  ) => void;
  onUpdateFeedTypeUtilisation?: (
    animalGroupId: string,
    feedTypeId: string,
    percent: number
  ) => void;
  onUpdateGrazingUtilisation?: (animalGroupId: string, percent: number) => void;
  onUpdateFieldFeedingUtilisation?: (
    animalGroupId: string,
    percent: number
  ) => void;
  onAddSupplementFeedType: (
    animalGroupId: string,
    name: string,
    substitutionRate: number,
    id?: string
  ) => void;
  onRemoveSupplementFeedType: (
    animalGroupId: string,
    feedTypeId: string
  ) => void;
  onUpdateHousing: (
    animalGroupId: string,
    stockClassId: string,
    month: number,
    yearOffset: 0 | 1,
    isGrazing: boolean
  ) => void;
  onUpdateMonthlyArea: (
    animalGroupId: string,
    month: number,
    areaHa: number
  ) => void;
  onUpdateGrowthRate: (month: number, yearOffset: 0 | 1, value: number) => void;
  isDirty?: boolean;
  isSaving?: boolean;
  onSave?: () => void | Promise<void>;
  onDiscard?: () => void;
  onAddStockClass: (
    animalGroupId: string,
    name: string,
    defaultStockClassName: string,
    initialValues: {
      animalCount: number;
      weightKg: number;
      allocationPercent?: number;
      allocationByMonth?: number[];
      animalCountByMonth?: number[];
    }
  ) => void;
  onRemoveStockClass: (animalGroupId: string, stockClassId: string) => void;
  onRenameStockClass: (
    animalGroupId: string,
    stockClassId: string,
    name: string
  ) => void;
}

export default function AnimalGroupDetailDialog({
  open,
  onClose,
  budget,
  grazingMonths,
  groupRows,
  groupRowsYear1,
  stockClassComputed,
  stockClassComputedYear1,
  actualGrowthRatesKgHaDay,
  currentMonth,
  feedStock = [],
  planName = "",
  planYear,
  planStartDate,
  planEndDate,
  initialViewYear,
  onExtendPlan,
  onUpdateStockClass,
  onUpdateSupplementAmount,
  onUpdateSupplementUtilisation,
  onUpdateFeedTypeUtilisation,
  onUpdateGrazingUtilisation,
  onUpdateFieldFeedingUtilisation,
  onAddSupplementFeedType,
  onRemoveSupplementFeedType,
  onUpdateHousing,
  onUpdateMonthlyArea,
  onUpdateGrowthRate,
  isDirty = false,
  isSaving = false,
  onSave,
  onDiscard,
  onAddStockClass,
  onRemoveStockClass,
  onRenameStockClass,
}: Props) {
  const getStockTonnes = (label: string): number | null => {
    const match = feedStock.find(
      (r) => r.type.toLowerCase() === label.toLowerCase()
    );
    return match ? match.stockTonnes : null;
  };
  const concentratesStock = feedStock.find((r) =>
    r.type.toLowerCase().includes("concentrat")
  );
  const safeCurrentMonth = Math.max(0, Math.min(11, currentMonth));
  const currentGroupRow =
    groupRows.find((r) => r.month === safeCurrentMonth) ?? groupRows[0];
  const totalAnimals = budget.stockClassPlans.reduce((sum, sc) => {
    const monthEntry =
      sc.months.find((m) => m.month === safeCurrentMonth) ?? sc.months[0];
    return sum + (monthEntry?.animalCount ?? 0);
  }, 0);
  const livestockUnits = budget.stockClassPlans.reduce((sum, sc) => {
    const monthEntry =
      sc.months.find((m) => m.month === safeCurrentMonth) ?? sc.months[0];
    const conversionRate = sc.defaultStockClassName
      ? {
          DAIRY_COW: 1.0,
          DAIRY_COW_2_KGMS: 1.0,
          SUCKLER_COW: 0.85,
          GROWING_CATTLE: 0.6,
          EWE: 0.15,
          EWE_TWINS: 0.2,
          WEANED_LAMB: 0.1,
        }[sc.defaultStockClassName] ?? 0.5
      : 0.5;
    return sum + (monthEntry?.animalCount ?? 0) * conversionRate;
  }, 0);
  const currentDailyFeedDemandKg = currentGroupRow?.totalDemandKgDay ?? 0;
  const currentForageAvailableTonnes =
    budget.grazingAreaHa > 0 && currentGroupRow
      ? (currentGroupRow.supplyKgHaDay *
          budget.grazingAreaHa *
          DEFAULT_GRAZING_DAYS[safeCurrentMonth]) /
        1000
      : 0;
  const { notify } = useNotification();
  const { data: allFields = [] } = useFields();
  const assignedFieldCount = allFields.filter(
    (field) => field.animalGroup === budget.animalGroupId
  ).length;

  // Year navigation
  const planStartDateObj = useMemo(
    () => (planStartDate ? new Date(planStartDate) : null),
    [planStartDate]
  );
  const planEndDateObj = useMemo(
    () => (planEndDate ? new Date(planEndDate) : null),
    [planEndDate]
  );
  const planStartYear =
    planStartDateObj?.getFullYear() ?? new Date().getFullYear();
  const planEndYear = planEndDateObj?.getFullYear() ?? new Date().getFullYear();
  const [viewYear, setViewYear] = useState(initialViewYear ?? planStartYear);
  const canGoPrevYear = viewYear > planStartYear;
  const canGoNextYear = viewYear < planEndYear;
  const hasYearNav = planStartDate != null && planEndDate != null;

  const visibleMonths = useMemo((): Set<number> => {
    if (!planStartDateObj || !planEndDateObj)
      return new Set(Array.from({ length: 12 }, (_, i) => i));
    const startYear = planStartDateObj.getFullYear();
    const endYear = planEndDateObj.getFullYear();
    const startMonth = planStartDateObj.getMonth();
    const endMonth = planEndDateObj.getMonth();
    const visible = new Set<number>();
    for (let m = 0; m < 12; m++) {
      const afterStart =
        viewYear > startYear || (viewYear === startYear && m >= startMonth);
      const beforeEnd =
        viewYear < endYear || (viewYear === endYear && m <= endMonth);
      if (afterStart && beforeEnd) visible.add(m);
    }
    return visible;
  }, [planStartDateObj, planEndDateObj, viewYear]);

  // When viewing a year beyond plan start, use year-1 computed data
  const viewYearOffset: 0 | 1 = viewYear > planStartYear ? 1 : 0;
  const activeGroupRows =
    viewYearOffset === 1 ? groupRowsYear1 ?? [] : groupRows;
  const activeStockClassComputed =
    viewYearOffset === 1
      ? stockClassComputedYear1 ?? new Map()
      : stockClassComputed;

  const visibleGroupRows = useMemo(
    () => activeGroupRows.filter((r) => visibleMonths.has(r.month)),
    [activeGroupRows, visibleMonths]
  );
  const visibleMonthLabels = useMemo(
    () => MONTH_LABELS.filter((_, i) => visibleMonths.has(i)),
    [visibleMonths]
  );
  const visibleSupplementPlans = useMemo(
    () => budget.supplementPlans.filter((s) => visibleMonths.has(s.month)),
    [budget.supplementPlans, visibleMonths]
  );

  // Add stock class dialog
  const [addScOpen, setAddScOpen] = useState(false);
  const [assignFieldOpen, setAssignFieldOpen] = useState(false);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  const [printSheetOpen, setPrintSheetOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const handleClose = () => {
    if (isDirty) {
      setConfirmCloseOpen(true);
    } else {
      onClose();
    }
  };

  const handleSave = async () => {
    if (!onSave) return;
    await onSave();
  };

  const handleSaveAndClose = async () => {
    setConfirmCloseOpen(false);
    if (!onSave) {
      onClose();
      return;
    }
    try {
      await onSave();
      onClose();
    } catch {
      // Offline guard and savePlan surface user-facing errors; keep dialog open.
    }
  };

  // Local state for advanced settings form — initialized when dialog opens
  const [localHousingUtils, setLocalHousingUtils] = useState<
    Record<string, number>
  >({});
  const [localGrazingUtil, setLocalGrazingUtil] = useState<number>(
    DEFAULT_GRAZING_FIELD_UTILISATION_PERCENT
  );
  const [localFieldUtil, setLocalFieldUtil] = useState<number>(
    DEFAULT_FIELD_FEEDING_UTILISATION_PERCENT
  );

  useEffect(() => {
    if (advancedSettingsOpen) {
      const init: Record<string, number> = {};
      for (const ft of budget.supplementFeedTypes) {
        init[ft.id] =
          budget.feedTypeUtilisation?.[ft.id] ??
          getDefaultHousedUtilisation(ft.id);
      }
      setLocalHousingUtils(init);
      setLocalGrazingUtil(
        budget.grazingUtilisationPercent ??
          DEFAULT_GRAZING_FIELD_UTILISATION_PERCENT
      );
      setLocalFieldUtil(
        budget.fieldFeedingUtilisationPercent ??
          DEFAULT_FIELD_FEEDING_UTILISATION_PERCENT
      );
    }
  }, [advancedSettingsOpen, budget]);

  // Add roughage menu anchor
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  // Column hover state
  const [hoverCol, setHoverCol] = useState<number | null>(null);
  const enterCol = useCallback((i: number) => setHoverCol(i), []);
  const leaveCol = useCallback(() => setHoverCol(null), []);

  /** Hover sx for a month cell — also removes borders */
  const colSx = (i: number) =>
    hoverCol === i ? { ...noBorder, backgroundColor: COL_HOVER_BG } : noBorder;

  /** Hover sx for cells inside ColorGroup (borders handled by parent CSS) */
  const hoverSx = (i: number) =>
    hoverCol === i ? { backgroundColor: COL_HOVER_BG } : undefined;

  // Build chart data — split roughage vs concentrates for distinct coloring
  const currentAreaHa = currentGroupRow?.grazingAreaHa ?? budget.grazingAreaHa;

  const chartData = visibleGroupRows.map((r, i) => {
    const supplement = visibleSupplementPlans[i];
    const grazingDays = DEFAULT_GRAZING_DAYS[r.month];
    const area = r.grazingAreaHa;
    const toKgDay = (tonnes: number, util: number) =>
      grazingDays > 0 ? (tonnes * 1000 * (util / 100)) / grazingDays : 0;
    const fieldUtil =
      budget.fieldFeedingUtilisationPercent ??
      DEFAULT_FIELD_FEEDING_UTILISATION_PERCENT;
    const getUtil = (feedTypeId: string) =>
      r.housingStatus === "housed"
        ? budget.feedTypeUtilisation?.[feedTypeId] ??
          getDefaultHousedUtilisation(feedTypeId)
        : fieldUtil;
    const concentratesType = budget.supplementFeedTypes.find(
      (ft) => ft.id === CONCENTRATES_FEED_TYPE_ID
    );
    const roughageKgDay = budget.supplementFeedTypes
      .filter((ft) => ft.id !== CONCENTRATES_FEED_TYPE_ID)
      .reduce((sum, ft) => {
        const amount = supplement?.amounts?.[ft.id] ?? 0;
        return sum + toKgDay(amount * ft.substitutionRate, getUtil(ft.id));
      }, 0);
    const concentratesAmount =
      supplement?.amounts?.[CONCENTRATES_FEED_TYPE_ID] ?? 0;
    const concentratesKgDay = toKgDay(
      concentratesAmount * (concentratesType?.substitutionRate ?? 0.5),
      getUtil(CONCENTRATES_FEED_TYPE_ID)
    );
    return {
      forage: r.supplyKgHaDay * area,
      roughage: roughageKgDay,
      concentrates: concentratesKgDay,
      demand: r.totalDemandKgDay,
    };
  });

  /** Per-month supplement breakdown for tooltips */
  const supplementBreakdown = visibleGroupRows.map((r, i) => {
    const supplement = visibleSupplementPlans[i];
    const grazingDays = DEFAULT_GRAZING_DAYS[r.month];
    const gm =
      grazingMonths.find(
        (g) => g.month === r.month && (g.yearOffset ?? 0) === viewYearOffset
      ) ?? grazingMonths.find((g) => g.month === r.month);
    const utilisationPct =
      budget.grazingUtilisationPercent ??
      gm?.utilisationPercent ??
      DEFAULT_GRAZING_FIELD_UTILISATION_PERCENT;
    const hasActual =
      viewYearOffset === 0 &&
      viewYear === new Date().getFullYear() &&
      r.month < currentMonth &&
      (actualGrowthRatesKgHaDay[r.month] ?? 0) > 0;
    const rawGrowthKgHaDay = hasActual
      ? actualGrowthRatesKgHaDay[r.month]
      : gm?.growthRateKgHaDay ?? 0;
    const area = r.grazingAreaHa;
    const fieldUtil =
      budget.fieldFeedingUtilisationPercent ??
      DEFAULT_FIELD_FEEDING_UTILISATION_PERCENT;
    const getUtil = (feedTypeId: string) =>
      r.housingStatus === "housed"
        ? budget.feedTypeUtilisation?.[feedTypeId] ??
          getDefaultHousedUtilisation(feedTypeId)
        : fieldUtil;

    const suppLines: {
      name: string;
      kgDay: number;
      tonnesMonth: number;
      util: number;
      effKgDay: number;
    }[] = [];
    for (const ft of budget.supplementFeedTypes) {
      const amount = supplement?.amounts?.[ft.id] ?? 0;
      if (amount === 0) continue;
      const util = getUtil(ft.id);
      const kgDay = grazingDays > 0 ? (amount * 1000) / grazingDays : 0;
      const effKgDay = kgDay * ft.substitutionRate * (util / 100);
      suppLines.push({
        name: ft.name,
        kgDay,
        tonnesMonth: amount,
        util,
        effKgDay,
      });
    }

    const balance = r.netDifferenceKgHaDay * area;
    const row = (label: string, value: string, muted = false) => (
      <Box
        key={label}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          opacity: muted ? 0.65 : 1,
        }}
      >
        <span>{label}</span>
        <span style={{ fontWeight: 600 }}>{value}</span>
      </Box>
    );

    return (
      <Box sx={{ minWidth: 220, fontSize: "0.78rem", lineHeight: 1.6 }}>
        <Box
          sx={{
            fontWeight: 700,
            mb: 0.5,
            fontSize: "0.72rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            opacity: 0.6,
          }}
        >
          Supply
        </Box>
        {r.housingStatus === "housed" ? (
          row("Grass", "none (housed)", true)
        ) : (
          <>
            {row(
              "Grass",
              `${Math.round(r.supplyKgHaDay * area).toLocaleString()} kg DM/day`
            )}
            <Box sx={{ opacity: 0.55, fontSize: "0.7rem", pl: 1, mb: 0.25 }}>
              {Math.round(rawGrowthKgHaDay)} kg/ha/day × {utilisationPct}% util
              × {area.toFixed(1)} ha
            </Box>
          </>
        )}
        {suppLines.map((s) => (
          <Box key={s.name}>
            {row(
              s.name,
              `${Math.round(s.effKgDay).toLocaleString()} kg DM/day`
            )}
            <Box sx={{ opacity: 0.55, fontSize: "0.7rem", pl: 1, mb: 0.25 }}>
              {Math.round(s.kgDay)} kg/day · {s.util}% util ·{" "}
              {Math.round(s.tonnesMonth * 10) / 10}t/month
            </Box>
          </Box>
        ))}
        {suppLines.length === 0 && row("Supplements", "none", true)}
        <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.2)", my: 0.75 }} />
        <Box
          sx={{
            fontWeight: 700,
            mb: 0.5,
            fontSize: "0.72rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            opacity: 0.6,
          }}
        >
          Demand
        </Box>
        {row(
          "Animals",
          `${Math.round(r.totalDemandKgDay).toLocaleString()} kg DM/day`
        )}
        <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.2)", my: 0.75 }} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            fontWeight: 700,
            color: balance < 0 ? "#fca5a5" : "#86efac",
          }}
        >
          <span>Balance</span>
          <span>
            {balance > 0 ? "+" : ""}
            {Math.round(balance).toLocaleString()} kg DM/day
          </span>
        </Box>
        {r.housingStatus !== "housed" && r.supplyKgHaDay > 0 && (
          <Box sx={{ opacity: 0.65, fontSize: "0.7rem", mt: 0.5 }}>
            Area for full grass cover:{" "}
            <strong>
              {(r.totalDemandKgDay / r.supplyKgHaDay).toFixed(1)} ha
            </strong>
          </Box>
        )}
      </Box>
    );
  });

  const yMaxRaw = Math.max(
    ...chartData.map((d) =>
      Math.max(d.demand, d.forage + d.roughage + d.concentrates)
    ),
    1
  );
  // Round up to a nice number appropriate for the magnitude
  const yMaxMagnitude = Math.pow(10, Math.floor(Math.log10(yMaxRaw)));
  const yMaxStep = yMaxMagnitude >= 100 ? yMaxMagnitude : 10;
  const yMax = Math.ceil(yMaxRaw / yMaxStep) * yMaxStep;

  const scale = (v: number) => (v / yMax) * CHART_HEIGHT;

  /** Inline spacer row (for use inside a <TableBody>) */
  const spacerRow = (
    <TableRow>
      <TableCell
        colSpan={2}
        sx={{
          p: 0,
          height: 12,
          border: "none",
          position: "sticky",
          left: 0,
          backgroundColor: "#fff",
          zIndex: 10,
        }}
      />
      {Array.from({ length: visibleMonths.size }, (_, i) => (
        <TableCell
          key={i}
          onMouseEnter={() => enterCol(i)}
          onMouseLeave={leaveCol}
          sx={{
            p: 0,
            height: 12,
            border: "none",
            backgroundColor: hoverCol === i ? COL_HOVER_BG : "transparent",
            transition: "background-color 0.1s",
          }}
        />
      ))}
    </TableRow>
  );

  return (
    <>
      <Dialog
        open={confirmCloseOpen}
        onClose={() => setConfirmCloseOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1rem" }}>
          Unsaved changes
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You have unsaved changes to this animal group. Do you want to save
            before closing?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmCloseOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmCloseOpen(false);
              onDiscard?.();
              onClose();
            }}
            color="error"
            sx={{ textTransform: "none" }}
          >
            Discard
          </Button>
          {onSave && (
            <Button
              onClick={() => {
                void handleSaveAndClose();
              }}
              variant="contained"
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Save & Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <DetailDialog
        open={open}
        onClose={handleClose}
        title={
          <>
            <Box
              component="span"
              sx={{ color: "text.secondary", fontWeight: 400 }}
            >
              Feed budget:{" "}
            </Box>
            {budget.animalGroupName}
          </>
        }
        headerActions={
          <Stack direction="row" spacing={1} alignItems="center">
            {isDirty && (
              <Chip size="small" color="warning" label="Unsaved changes" />
            )}
            {onSave && (
              <Tooltip title="Save changes">
                <span>
                  <IconButton
                    size="small"
                    disabled={!isDirty || isSaving}
                    onClick={() => {
                      void handleSave().catch(() => {});
                    }}
                    sx={{
                      backgroundColor: isDirty ? "#6B4FA0" : undefined,
                      color: isDirty ? "#fff" : undefined,
                      "&:hover": {
                        backgroundColor: isDirty ? "#5a3d8a" : undefined,
                      },
                      "&.Mui-disabled": {
                        backgroundColor: undefined,
                        color: undefined,
                      },
                    }}
                  >
                    <SaveOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Print feeding sheet">
              <IconButton
                size="small"
                onClick={() => setPrintSheetOpen(true)}
                sx={{
                  backgroundColor: "#F1EFF7",
                  color: "#7D5DA3",
                  "&:hover": { backgroundColor: "#E5E1F0" },
                }}
              >
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => setAdvancedSettingsOpen(true)}
              sx={{
                backgroundColor: "#F1EFF7",
                color: "#7D5DA3",
                "&:hover": { backgroundColor: "#E5E1F0" },
              }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
        contentSx={{ backgroundColor: "#F7FCF1" }}
      >
        <Box
          sx={{
            maxWidth: 1440,
            mx: "auto",
            width: "100%",
            pb: { xs: "calc(70px + env(safe-area-inset-bottom))", md: 0 },
          }}
        >
          <Box
            sx={{
              ...SECTION_CARD_SX,
              mb: 2,
              borderRadius: 2,
              border: "1px solid #E5E7EB",
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.68rem",
                  mb: 0.75,
                  display: "block",
                }}
              >
                Current month — {MONTH_LABELS[safeCurrentMonth]}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 0.75,
                }}
              >
                {[
                  {
                    label: "Animals in group",
                    value: `${totalAnimals}`,
                    unit: `${Math.round(livestockUnits)} LU`,
                  },
                  {
                    label: "Grazing area",
                    value: `${currentAreaHa.toFixed(1)}`,
                    unit: "Ha",
                  },
                  {
                    label: "Daily feed demand",
                    value: `${Math.round(
                      currentDailyFeedDemandKg
                    ).toLocaleString()}`,
                    unit: "kgDM/day",
                  },
                ].map((metric) => (
                  <Box
                    key={metric.label}
                    sx={{
                      ...METRIC_CARD_SX,
                      minWidth: 0,
                      px: { xs: 1, sm: 1.25 },
                      py: { xs: 0.35, sm: 0.4 },
                    }}
                  >
                    <Box
                      sx={{
                        py: 0.5,
                        borderBottom: "1px solid #F0F0F0",
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            fontSize: { xs: "0.6rem", sm: "0.68rem" },
                          }}
                        >
                          {metric.label}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: "#1a1a2e",
                            lineHeight: 1.15,
                            fontSize: { xs: "1.05rem", sm: "1.5rem" },
                          }}
                        >
                          {metric.value}
                        </Typography>
                        {metric.unit && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: { xs: "0.62rem", sm: "0.75rem" },
                            }}
                          >
                            {metric.unit}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          {/* Unified chart + summary table */}
          <Box sx={SECTION_CARD_SX}>
            {hasYearNav && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                <IconButton
                  size="small"
                  disabled={!canGoPrevYear}
                  onClick={() => setViewYear((y) => y - 1)}
                  sx={{ p: 0.5 }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    minWidth: 52,
                    textAlign: "center",
                    fontSize: "1rem",
                    color: "#18181b",
                    userSelect: "none",
                  }}
                >
                  {viewYear}
                </Typography>
                <IconButton
                  size="small"
                  disabled={!canGoNextYear}
                  onClick={() => setViewYear((y) => y + 1)}
                  sx={{ p: 0.5 }}
                >
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}
            <TableContainer sx={{ overflowX: "auto" }}>
              <BudgetTable size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        minWidth: { xs: 108, sm: 170 },
                        width: { xs: 108, sm: 170 },
                        border: "none",
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#fff",
                        zIndex: 11,
                        px: { xs: 0.5, sm: 1 },
                      }}
                    />
                    <TableCell
                      sx={{
                        ...stickySpacerSx,
                        border: "none",
                        zIndex: 11,
                      }}
                    />
                    {visibleMonthLabels.map((m, i) => (
                      <MonthHeaderCell
                        key={m}
                        onMouseEnter={() => enterCol(i)}
                        onMouseLeave={leaveCol}
                        sx={{
                          ...colSx(i),
                          px: { xs: 0.75, sm: 1 },
                        }}
                      >
                        {m}
                      </MonthHeaderCell>
                    ))}
                  </TableRow>
                </TableHead>

                {/* ── Housing row — just above chart ── */}
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        minWidth: { xs: 108, sm: 170 },
                        width: { xs: 108, sm: 170 },
                        border: "none",
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#fff",
                        zIndex: 11,
                        px: { xs: 0.5, sm: 1 },
                        py: 0.5,
                        fontSize: "0.7rem",
                        color: "text.secondary",
                      }}
                    >
                      Housing
                    </TableCell>
                    <TableCell
                      sx={{ ...stickySpacerSx, border: "none", zIndex: 11 }}
                    />
                    {visibleGroupRows.map((c, i) => {
                      const isGrazing = c.housingStatus !== "housed";
                      return (
                        <TableCell
                          key={c.month}
                          onMouseEnter={() => enterCol(i)}
                          onMouseLeave={leaveCol}
                          sx={{
                            ...hoverSx(i),
                            border: "none",
                            px: { xs: 0.5, sm: 0.75 },
                            py: 0.5,
                            textAlign: "center",
                          }}
                        >
                          <Box
                            onClick={() =>
                              budget.stockClassPlans.forEach((sc) =>
                                onUpdateHousing(
                                  budget.animalGroupId,
                                  sc.stockClassId,
                                  c.month,
                                  viewYearOffset,
                                  !isGrazing
                                )
                              )
                            }
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              px: 0.5,
                              py: 0.15,
                              borderRadius: 1,
                              fontSize: "0.62rem",
                              fontWeight: 700,
                              backgroundColor: isGrazing
                                ? "#dcfce7"
                                : "#f3f4f6",
                              color: isGrazing ? "#15803d" : "#6b7280",
                              border: `1px solid ${
                                isGrazing ? "#86efac" : "#d1d5db"
                              }`,
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                              userSelect: "none",
                              "&:hover": { opacity: 0.75 },
                            }}
                          >
                            {isGrazing ? "Grazing" : "Housed"}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>

                {/* ── Chart section ── */}
                <TableBody>
                  {spacerRow}
                  <TableRow>
                    {/* Legend — sticky, z-index above chart SVG lines */}
                    <TableCell
                      sx={{
                        verticalAlign: "top",
                        p: 0.5,
                        border: "none",
                        height: CHART_HEIGHT,
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#fff",
                        zIndex: 10,
                      }}
                    >
                      <Stack spacing={0.3}>
                        {[
                          { color: COLORS.dahlia[500], label: "Grass" },
                          { color: COLORS.dahlia[300], label: "Roughage" },
                          { color: COLORS.daisy[300], label: "Concentrates" },
                        ].map((item) => (
                          <Stack
                            key={item.label}
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                backgroundColor: item.color,
                                borderRadius: 0.5,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontSize={9}
                              lineHeight={1.1}
                            >
                              {item.label}
                            </Typography>
                          </Stack>
                        ))}
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 2,
                              backgroundColor: COLORS.bellflower[700],
                              borderRadius: 1,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontSize={9}
                            lineHeight={1.1}
                          >
                            Demand
                          </Typography>
                        </Stack>
                      </Stack>
                    </TableCell>
                    {/* Y-axis labels — sticky, z-index above chart SVG lines */}
                    <TableCell
                      sx={{
                        ...stickySpacerSx,
                        pr: 0.25,
                        border: "none",
                        verticalAlign: "top",
                        height: CHART_HEIGHT,
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          alignItems: "flex-end",
                          py: 0.5,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="caption"
                            fontSize={8}
                            color="text.disabled"
                            sx={{
                              display: "block",
                              textAlign: "right",
                              lineHeight: 1.1,
                              mb: 0.25,
                            }}
                          >
                            kg/day
                          </Typography>
                          <Typography
                            variant="caption"
                            fontSize={9}
                            color="text.disabled"
                          >
                            {yMax.toLocaleString()}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          fontSize={9}
                          color="text.disabled"
                        >
                          {Math.round(yMax / 2).toLocaleString()}
                        </Typography>
                        <Typography
                          variant="caption"
                          fontSize={9}
                          color="text.disabled"
                        >
                          0
                        </Typography>
                      </Box>
                    </TableCell>
                    {chartData.map((d, i) => (
                      <Tooltip
                        key={i}
                        title={supplementBreakdown[i]}
                        arrow
                        placement="top"
                      >
                        <TableCell
                          onMouseEnter={() => enterCol(i)}
                          onMouseLeave={leaveCol}
                          sx={{
                            p: 0,
                            position: "relative",
                            height: CHART_HEIGHT,
                            borderBottom: "1px solid #e0e0e0",
                            borderTop: "none",
                            borderLeft: "none",
                            borderRight: "none",
                            verticalAlign: "bottom",
                            backgroundColor:
                              hoverCol === i ? COL_HOVER_BG : "transparent",
                            transition: "background-color 0.1s",
                          }}
                        >
                          {/* Gridlines */}
                          {[0, 0.5].map((frac) => (
                            <Box
                              key={frac}
                              sx={{
                                position: "absolute",
                                top: `${frac * 100}%`,
                                left: 0,
                                right: 0,
                                borderTop: "1px solid #f0f0f0",
                                pointerEvents: "none",
                              }}
                            />
                          ))}
                          {/* Stacked bar */}
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 0,
                              left: "12%",
                              right: "12%",
                              display: "flex",
                              flexDirection: "column-reverse",
                            }}
                          >
                            <Box
                              sx={{
                                height: scale(d.forage),
                                backgroundColor: COLORS.dahlia[500],
                                borderRadius: "2px 2px 0 0",
                              }}
                            />
                            <Box
                              sx={{
                                height: scale(d.roughage),
                                backgroundColor: COLORS.dahlia[300],
                              }}
                            />
                            <Box
                              sx={{
                                height: scale(d.concentrates),
                                backgroundColor: COLORS.daisy[300],
                                borderRadius: "2px 2px 0 0",
                              }}
                            />
                          </Box>
                          {/* Demand dot */}
                          <Box
                            sx={{
                              position: "absolute",
                              left: "50%",
                              bottom: scale(d.demand),
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              backgroundColor: COLORS.bellflower[700],
                              transform: "translate(-50%, 50%)",
                              zIndex: 2,
                            }}
                          />
                          {/* Demand line segment to next cell */}
                          {i < chartData.length - 1 && (
                            <svg
                              style={{
                                position: "absolute",
                                bottom: 0,
                                left: "50%",
                                width: "100%",
                                height: CHART_HEIGHT,
                                pointerEvents: "none",
                                overflow: "visible",
                                zIndex: 1,
                              }}
                            >
                              <line
                                x1={0}
                                y1={CHART_HEIGHT - scale(d.demand)}
                                x2="100%"
                                y2={
                                  CHART_HEIGHT - scale(chartData[i + 1].demand)
                                }
                                stroke={COLORS.bellflower[700]}
                                strokeWidth={2.5}
                              />
                            </svg>
                          )}
                        </TableCell>
                      </Tooltip>
                    ))}
                  </TableRow>
                  {spacerRow}
                </TableBody>

                {/* ── Group 1: Demand ── */}
                <ColorGroup color={COLORS.bellflower[700]}>
                  <TableRow>
                    <Tooltip
                      title="Total animal demand ÷ grazing area. Increases with more animals or higher allocation; decreases with larger area (lower stocking density)."
                      arrow
                      placement="right"
                    >
                      <LabelCell
                        colSpan={2}
                        sx={{ px: { xs: 1, sm: 1.25 }, cursor: "help" }}
                      >
                        Demand (kg DM/day)
                      </LabelCell>
                    </Tooltip>
                    {visibleGroupRows.map((c, i) => (
                      <ValueCell
                        key={c.month}
                        onMouseEnter={() => enterCol(i)}
                        onMouseLeave={leaveCol}
                        sx={hoverSx(i)}
                      >
                        {Math.round(c.totalDemandKgDay).toLocaleString()}
                      </ValueCell>
                    ))}
                  </TableRow>
                </ColorGroup>

                {/* ── Group 2: Growth + Forage ── */}
                <SpacerSection
                  hoverCol={hoverCol}
                  enterCol={enterCol}
                  leaveCol={leaveCol}
                  colCount={visibleMonths.size}
                />
                <ColorGroup color={COLORS.dahlia[500]}>
                  <TableRow>
                    <LabelCell
                      colSpan={2}
                      sx={{
                        px: { xs: 1, sm: 1.25 },
                        "& .area-edit-btn": {
                          opacity: 0,
                          transition: "opacity 0.15s",
                        },
                        "&:hover .area-edit-btn, &:focus-within .area-edit-btn":
                          {
                            opacity: 1,
                          },
                        "@media (hover: none)": {
                          "& .area-edit-btn": { opacity: 1 },
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <span>
                          Area (ha, {assignedFieldCount}{" "}
                          {assignedFieldCount === 1 ? "field" : "fields"})
                        </span>
                        <Tooltip title="Edit fields">
                          <IconButton
                            className="area-edit-btn"
                            aria-label="Edit fields"
                            size="small"
                            onClick={() => setAssignFieldOpen(true)}
                            sx={{ p: 0.15, color: COLORS.dahlia[600] }}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 11 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </LabelCell>
                    {visibleGroupRows.map((r, i) => {
                      const isHoused = r.housingStatus === "housed";
                      const areaValue = isHoused
                        ? 0
                        : budget.monthlyGrazingAreaHa?.[r.month] ??
                          budget.grazingAreaHa;

                      if (!isHoused) {
                        return (
                          <EditableCell
                            key={r.month}
                            value={areaValue}
                            onChange={(v) =>
                              onUpdateMonthlyArea(
                                budget.animalGroupId,
                                r.month,
                                v
                              )
                            }
                            decimals={1}
                            step={0.5}
                            onMouseEnter={() => enterCol(i)}
                            onMouseLeave={leaveCol}
                            cellSx={hoverSx(i)}
                          />
                        );
                      }

                      return (
                        <ValueCell
                          key={r.month}
                          onMouseEnter={() => enterCol(i)}
                          onMouseLeave={leaveCol}
                          sx={{
                            ...hoverSx(i),
                            color: isHoused ? "text.disabled" : undefined,
                          }}
                        >
                          {areaValue.toFixed(1)}
                        </ValueCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <LabelCell colSpan={2} sx={{ px: { xs: 1, sm: 1.25 } }}>
                      Growth (kg DM/ha/day)
                    </LabelCell>
                    {visibleGroupRows.map((r, i) => {
                      const gm =
                        grazingMonths.find(
                          (g) =>
                            g.month === r.month &&
                            (g.yearOffset ?? 0) === viewYearOffset
                        ) ?? grazingMonths.find((g) => g.month === r.month);
                      const hasActual =
                        viewYearOffset === 0 &&
                        viewYear === new Date().getFullYear() &&
                        r.month < currentMonth &&
                        actualGrowthRatesKgHaDay[r.month] > 0;
                      if (hasActual) {
                        return (
                          <Tooltip
                            key={r.month}
                            title="Measured from field readings"
                            arrow
                            placement="bottom"
                          >
                            <ValueCell
                              onMouseEnter={() => enterCol(i)}
                              onMouseLeave={leaveCol}
                              sx={hoverSx(i)}
                            >
                              {Math.round(actualGrowthRatesKgHaDay[r.month])}
                            </ValueCell>
                          </Tooltip>
                        );
                      }
                      return (
                        <EditableCell
                          key={r.month}
                          value={gm?.growthRateKgHaDay ?? 0}
                          onChange={(v) =>
                            onUpdateGrowthRate(r.month, viewYearOffset, v)
                          }
                          decimals={0}
                          step={5}
                          onMouseEnter={() => enterCol(i)}
                          onMouseLeave={leaveCol}
                          cellSx={hoverSx(i)}
                        />
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <LabelCell colSpan={2} sx={{ px: { xs: 1, sm: 1.25 } }}>
                      Forage (kg DM/day)
                    </LabelCell>
                    {visibleGroupRows.map((c, i) => {
                      const forageKgDay = c.supplyKgHaDay * c.grazingAreaHa;
                      return (
                        <Tooltip
                          key={c.month}
                          title={
                            c.housingStatus === "housed"
                              ? "Animals are housed — no forage from pasture"
                              : `${c.grazingAreaHa.toFixed(1)} ha × growth × ${
                                  budget.grazingUtilisationPercent ??
                                  (
                                    grazingMonths.find(
                                      (g) =>
                                        g.month === c.month &&
                                        (g.yearOffset ?? 0) === viewYearOffset
                                    ) ??
                                    grazingMonths.find(
                                      (g) => g.month === c.month
                                    )
                                  )?.utilisationPercent ??
                                  DEFAULT_GRAZING_FIELD_UTILISATION_PERCENT
                                }% utilisation`
                          }
                          arrow
                          placement="bottom"
                        >
                          <ValueCell
                            onMouseEnter={() => enterCol(i)}
                            onMouseLeave={leaveCol}
                            sx={{
                              ...hoverSx(i),
                              color:
                                c.housingStatus === "housed"
                                  ? "text.disabled"
                                  : undefined,
                            }}
                          >
                            {Math.round(forageKgDay).toLocaleString()}
                          </ValueCell>
                        </Tooltip>
                      );
                    })}
                  </TableRow>
                </ColorGroup>

                {/* ── Group 3: Supplements (roughage + optional concentrates) ── */}
                {(() => {
                  const roughageTypes = budget.supplementFeedTypes.filter(
                    (ft) => ft.id !== CONCENTRATES_FEED_TYPE_ID
                  );
                  const concentratesType = budget.supplementFeedTypes.find(
                    (ft) => ft.id === CONCENTRATES_FEED_TYPE_ID
                  );
                  const existingNames = new Set(
                    budget.supplementFeedTypes.map((ft) =>
                      ft.name.toLowerCase()
                    )
                  );
                  const existingOptIds = new Set(
                    budget.supplementFeedTypes.map((ft) => ft.id.toUpperCase())
                  );
                  const availableRoughage = ROUGHAGE_SUPPLEMENT_OPTIONS.filter(
                    (o) =>
                      !existingNames.has(o.label.toLowerCase()) &&
                      !existingOptIds.has(o.id)
                  );
                  const showConcentratesOption = !concentratesType;
                  const allAdded =
                    availableRoughage.length === 0 && !showConcentratesOption;
                  return (
                    <>
                      <SpacerSection
                        hoverCol={hoverCol}
                        enterCol={enterCol}
                        leaveCol={leaveCol}
                        colCount={visibleMonths.size}
                      />
                      {roughageTypes.length > 0 && (
                        <ColorGroup color={COLORS.dahlia[300]}>
                          {roughageTypes.map((ft) => (
                            <TableRow key={ft.id}>
                              <LabelCell
                                colSpan={2}
                                sx={{ px: { xs: 0.75, sm: 1 }, py: 0 }}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  spacing={0.5}
                                >
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{ fontSize: "0.75rem" }}
                                  >
                                    {ft.name} (kg/day)
                                    {(() => {
                                      const stock = getStockTonnes(ft.name);
                                      return stock !== null ? (
                                        <Box
                                          component="span"
                                          sx={{
                                            color: "text.secondary",
                                            fontSize: "0.68rem",
                                            ml: 0.5,
                                          }}
                                        >
                                          {stock.toLocaleString(undefined, {
                                            maximumFractionDigits: 1,
                                          })}
                                          t in stock
                                        </Box>
                                      ) : null;
                                    })()}
                                  </Typography>
                                  <Tooltip title="Remove">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        onRemoveSupplementFeedType(
                                          budget.animalGroupId,
                                          ft.id
                                        )
                                      }
                                      sx={{
                                        p: 0.25,
                                        color: "#9ca3af",
                                        flexShrink: 0,
                                        "&:hover": { color: "#ef4444" },
                                      }}
                                    >
                                      <CloseIcon sx={{ fontSize: 13 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </LabelCell>
                              {visibleSupplementPlans.map((s, i) => {
                                const days = DEFAULT_GRAZING_DAYS[s.month];
                                const storedTonnes = s.amounts?.[ft.id] ?? 0;
                                const displayKgDay =
                                  days > 0 ? (storedTonnes * 1000) / days : 0;
                                return (
                                  <EditableCell
                                    key={s.month}
                                    value={displayKgDay}
                                    onChange={(v) =>
                                      onUpdateSupplementAmount(
                                        budget.animalGroupId,
                                        s.month,
                                        ft.id,
                                        days > 0 ? (v * days) / 1000 : 0
                                      )
                                    }
                                    decimals={0}
                                    step={10}
                                    onMouseEnter={() => enterCol(i)}
                                    onMouseLeave={leaveCol}
                                    cellSx={hoverSx(i)}
                                  />
                                );
                              })}
                            </TableRow>
                          ))}
                        </ColorGroup>
                      )}
                      {roughageTypes.length > 0 && concentratesType && (
                        <TableBody>{spacerRow}</TableBody>
                      )}
                      {concentratesType && (
                        <ColorGroup color={COLORS.daisy[300]}>
                          <TableRow>
                            <LabelCell
                              colSpan={2}
                              sx={{ px: { xs: 0.75, sm: 1 }, py: 0 }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={0.5}
                              >
                                <Typography
                                  variant="body2"
                                  noWrap
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  Concentrates (kg/day)
                                  {concentratesStock != null && (
                                    <Box
                                      component="span"
                                      sx={{
                                        color: "text.secondary",
                                        fontSize: "0.68rem",
                                        ml: 0.5,
                                      }}
                                    >
                                      {concentratesStock.stockTonnes.toLocaleString(
                                        undefined,
                                        { maximumFractionDigits: 1 }
                                      )}
                                      t in stock
                                    </Box>
                                  )}
                                </Typography>
                                <Tooltip title="Remove">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      onRemoveSupplementFeedType(
                                        budget.animalGroupId,
                                        CONCENTRATES_FEED_TYPE_ID
                                      )
                                    }
                                    sx={{
                                      p: 0.25,
                                      color: "#9ca3af",
                                      flexShrink: 0,
                                      "&:hover": { color: "#ef4444" },
                                    }}
                                  >
                                    <CloseIcon sx={{ fontSize: 13 }} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </LabelCell>
                            {visibleSupplementPlans.map((s, i) => {
                              const days = DEFAULT_GRAZING_DAYS[s.month];
                              const storedTonnes =
                                s.amounts?.[CONCENTRATES_FEED_TYPE_ID] ?? 0;
                              const displayKgDay =
                                days > 0 ? (storedTonnes * 1000) / days : 0;
                              return (
                                <EditableCell
                                  key={s.month}
                                  value={displayKgDay}
                                  onChange={(v) =>
                                    onUpdateSupplementAmount(
                                      budget.animalGroupId,
                                      s.month,
                                      CONCENTRATES_FEED_TYPE_ID,
                                      days > 0 ? (v * days) / 1000 : 0
                                    )
                                  }
                                  decimals={1}
                                  step={10}
                                  onMouseEnter={() => enterCol(i)}
                                  onMouseLeave={leaveCol}
                                  cellSx={hoverSx(i)}
                                />
                              );
                            })}
                          </TableRow>
                        </ColorGroup>
                      )}
                      {/* Add supplement row */}
                      <TableBody>
                        <TableRow>
                          <TableCell
                            colSpan={2 + visibleMonths.size}
                            sx={{
                              border: "none",
                              py: 0.5,
                              pl: { xs: 1, sm: 1.25 },
                            }}
                          >
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                              sx={{
                                textTransform: "none",
                                color: "text.secondary",
                              }}
                            >
                              Add supplement
                            </Button>
                            <Menu
                              anchorEl={menuAnchorEl}
                              open={!!menuAnchorEl}
                              onClose={() => setMenuAnchorEl(null)}
                              slotProps={{ paper: { sx: { minWidth: 200 } } }}
                            >
                              {allAdded && (
                                <MenuItem disabled>
                                  All types already added
                                </MenuItem>
                              )}
                              {availableRoughage.map((o) => {
                                const stock = getStockTonnes(o.label);
                                return (
                                  <MenuItem
                                    key={o.id}
                                    onClick={() => {
                                      onAddSupplementFeedType(
                                        budget.animalGroupId,
                                        o.label,
                                        o.substitutionRate,
                                        o.id
                                      );
                                      setMenuAnchorEl(null);
                                    }}
                                    sx={{ fontSize: "0.875rem" }}
                                  >
                                    <Box sx={{ flex: 1 }}>{o.label}</Box>
                                    {stock !== null && (
                                      <Box
                                        component="span"
                                        sx={{
                                          color: "text.secondary",
                                          fontSize: "0.75rem",
                                          ml: 1,
                                        }}
                                      >
                                        {stock.toLocaleString(undefined, {
                                          maximumFractionDigits: 1,
                                        })}
                                        t
                                      </Box>
                                    )}
                                  </MenuItem>
                                );
                              })}
                              {showConcentratesOption &&
                                availableRoughage.length > 0 && (
                                  <Box
                                    component="li"
                                    sx={{
                                      borderTop: "1px solid #f0f0f0",
                                      mt: 0.5,
                                    }}
                                  />
                                )}
                              {showConcentratesOption && (
                                <MenuItem
                                  onClick={() => {
                                    onAddSupplementFeedType(
                                      budget.animalGroupId,
                                      "Concentrates",
                                      0.5,
                                      CONCENTRATES_FEED_TYPE_ID
                                    );
                                    setMenuAnchorEl(null);
                                  }}
                                  sx={{ fontSize: "0.875rem" }}
                                >
                                  <Box sx={{ flex: 1 }}>Concentrates</Box>
                                  {concentratesStock != null && (
                                    <Box
                                      component="span"
                                      sx={{
                                        color: "text.secondary",
                                        fontSize: "0.75rem",
                                        ml: 1,
                                      }}
                                    >
                                      {concentratesStock.stockTonnes.toLocaleString(
                                        undefined,
                                        { maximumFractionDigits: 1 }
                                      )}
                                      t
                                    </Box>
                                  )}
                                </MenuItem>
                              )}
                            </Menu>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </>
                  );
                })()}

                {/* ── Balance ── */}
                <TableBody>
                  {spacerRow}
                  <TableRow>
                    <LabelCell
                      colSpan={2}
                      sx={{
                        ...noBorder,
                        fontWeight: 700,
                        px: { xs: 1, sm: 1.25 },
                      }}
                    >
                      Balance (kg DM/day)
                    </LabelCell>
                    {visibleGroupRows.map((c, i) => {
                      const balanceKgDay =
                        c.netDifferenceKgHaDay * c.grazingAreaHa;
                      return (
                        <Tooltip
                          key={c.month}
                          title={supplementBreakdown[i]}
                          arrow
                          placement="top"
                        >
                          <ValueCell
                            onMouseEnter={() => enterCol(i)}
                            onMouseLeave={leaveCol}
                            sx={{
                              ...colSx(i),
                              fontWeight: 700,
                              color: balanceKgDay < 0 ? "#dc2626" : undefined,
                            }}
                          >
                            {balanceKgDay > 0 ? "+" : ""}
                            {Math.round(balanceKgDay).toLocaleString()}
                          </ValueCell>
                        </Tooltip>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </BudgetTable>
            </TableContainer>
          </Box>

          {/* Stock class section */}
          <Box sx={SECTION_CARD_SX}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color="text.secondary"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontSize: "0.75rem",
                }}
              >
                Stock Classes
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddScOpen(true)}
                sx={{ textTransform: "none" }}
              >
                Add Stock Class
              </Button>
            </Stack>

            <AddStockClassDialog
              open={addScOpen}
              onClose={() => setAddScOpen(false)}
              planStartDate={planStartDate}
              planEndDate={planEndDate}
              onExtendPlan={onExtendPlan}
              onAdd={(classes) => {
                classes.forEach((cls) =>
                  onAddStockClass(
                    budget.animalGroupId,
                    cls.name,
                    cls.defaultStockClassName,
                    cls.initialValues
                  )
                );
                setAddScOpen(false);
                const names = classes.map((c) => c.name).join(" and ");
                notify(`${names} added to ${budget.animalGroupName}`);
              }}
            />
            <AssignFieldDialog
              open={assignFieldOpen}
              close={() => setAssignFieldOpen(false)}
              animalGroupId={budget.animalGroupId}
              animalGroupName={budget.animalGroupName}
              onApplyArea={(areaHa, scope) => {
                const months =
                  scope === "all"
                    ? Array.from({ length: 12 }, (_, month) => month)
                    : [safeCurrentMonth];
                months.forEach((month) =>
                  onUpdateMonthlyArea(budget.animalGroupId, month, areaHa)
                );
              }}
            />
            <FeedingSheetPrintDialog
              open={printSheetOpen}
              onClose={() => setPrintSheetOpen(false)}
              budget={budget}
              groupRows={groupRows}
              stockClassComputed={stockClassComputed}
              grazingMonths={grazingMonths}
              planName={planName}
              planYear={planYear}
              defaultMonth={safeCurrentMonth}
            />

            {budget.stockClassPlans
              .filter((scPlan) =>
                scPlan.months.some(
                  (m) => (m.yearOffset ?? 0) === viewYearOffset
                )
              )
              .map((scPlan) => (
                <Box
                  key={scPlan.stockClassId}
                  sx={{
                    mb: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <StockClassMonthlyTable
                    stockClassPlan={scPlan}
                    computed={
                      activeStockClassComputed.get(scPlan.stockClassId) ?? []
                    }
                    visibleMonths={visibleMonths}
                    viewYearOffset={viewYearOffset}
                    onUpdate={(scId, month, yearOffset, field, value) =>
                      onUpdateStockClass(
                        budget.animalGroupId,
                        scId,
                        month,
                        yearOffset,
                        field,
                        value
                      )
                    }
                    onUpdateHousing={(scId, month, yearOffset, isGrazing) =>
                      onUpdateHousing(
                        budget.animalGroupId,
                        scId,
                        month,
                        yearOffset,
                        isGrazing
                      )
                    }
                    onRemove={(scId) =>
                      onRemoveStockClass(budget.animalGroupId, scId)
                    }
                    onRename={(scId, name) =>
                      onRenameStockClass(budget.animalGroupId, scId, name)
                    }
                  />
                </Box>
              ))}

            {budget.stockClassPlans.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 3, textAlign: "center" }}
              >
                No stock classes. Click "Add Stock Class" above.
              </Typography>
            )}
            {budget.stockClassPlans.length > 0 &&
              !budget.stockClassPlans.some((scPlan) =>
                scPlan.months.some(
                  (m) => (m.yearOffset ?? 0) === viewYearOffset
                )
              ) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 3, textAlign: "center" }}
                >
                  No stock classes with data in this year.
                </Typography>
              )}
          </Box>
        </Box>
        <Dialog
          open={advancedSettingsOpen}
          onClose={() => setAdvancedSettingsOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Advanced settings</DialogTitle>
          <DialogContent>
            <Box
              sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2.5 }}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{
                    mb: 1,
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Housed feeding utilisation
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {budget.supplementFeedTypes.map((ft) => (
                    <TextField
                      key={ft.id}
                      label={`${ft.name} (%)`}
                      type="number"
                      size="small"
                      fullWidth
                      value={
                        localHousingUtils[ft.id] ??
                        getDefaultHousedUtilisation(ft.id)
                      }
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setLocalHousingUtils((prev) => ({
                          ...prev,
                          [ft.id]: v,
                        }));
                      }}
                      slotProps={{ htmlInput: { min: 0, max: 100 } }}
                    />
                  ))}
                </Box>
              </Box>
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{
                    mb: 1,
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Field feeding utilisation
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <TextField
                    label="Grazing (%)"
                    type="number"
                    size="small"
                    fullWidth
                    value={localGrazingUtil}
                    onChange={(e) =>
                      setLocalGrazingUtil(Number(e.target.value))
                    }
                    helperText="Grass utilisation when animals are grazing."
                    slotProps={{ htmlInput: { min: 0, max: 100 } }}
                  />
                  <TextField
                    label="Supplements (%)"
                    type="number"
                    size="small"
                    fullWidth
                    value={localFieldUtil}
                    onChange={(e) => setLocalFieldUtil(Number(e.target.value))}
                    helperText="Supplement utilisation when field feeding / bale grazing."
                    slotProps={{ htmlInput: { min: 0, max: 100 } }}
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => {
                for (const [ftId, util] of Object.entries(localHousingUtils)) {
                  const clamped = Math.max(0, Math.min(100, util));
                  onUpdateFeedTypeUtilisation?.(
                    budget.animalGroupId,
                    ftId,
                    clamped
                  );
                }
                onUpdateGrazingUtilisation?.(
                  budget.animalGroupId,
                  Math.max(0, Math.min(100, localGrazingUtil))
                );
                onUpdateFieldFeedingUtilisation?.(
                  budget.animalGroupId,
                  Math.max(0, Math.min(100, localFieldUtil))
                );
                setAdvancedSettingsOpen(false);
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      </DetailDialog>
    </>
  );
}
